import React, { useState, useRef, useCallback, useMemo, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

//========== Import components from MUI Library ============
//All Wrapper Elements
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
//All CSS
import { makeStyles } from "tss-react/mui";
//All Element/ Controls
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import ListItemIcon from "@mui/material/ListItemIcon";

// All Icons
import Avatar from "@mui/material/Avatar";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import SportsScoreIcon from "@mui/icons-material/SportsScore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SaveIcon from "@mui/icons-material/Save";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

//========== Import components from ANT-Design Library ============
import MenuOutlined from "@ant-design/icons/MenuOutlined";
import NodeIndexOutlined from "@ant-design/icons/NodeIndexOutlined";
import InfoCircleFilled from "@ant-design/icons/InfoCircleFilled";
import { Tooltip, Progress } from "antd";

//========== Import components from our files ============
//All Sub Pages for Popup
import AddMapping from "./AddMapping";
import Template from "./Template/Template";
import UploadTemplate from "./Template/UploadTemplate";
import ErrorLog from "./Template/ErrorLog";
import AddField from "./AddField";

// All Common Element/ Controls
import NotAllowedDialog from "../../../../Common/NotAllowedDialog";
import { ButtonUI, ConfirmPopup } from "../../../../Common";
import DraggableTable from "../../../../Common/DraggableTable";

// Others
import { getUUID, setPrevState } from "../../../../../util/common";

import { addDataSources, showToast } from "../../../../../store/actions";
import { addEntities, setEntityFetching } from "../../../../../store/actions/entity";
import { setDsFetching } from "../../../../../store/actions/dataSource";

import DataSourceClass from "../../../../../service/dataSource";

import { DATA_SOURCE, OBJECT_TYPE } from "../../../../../util/Data/DataModeling";
import DSContext from "./DSContext";
import SmallToolbar from "../../../../Common/SmallToolbar";

const useStyles = makeStyles()({
  PanelContainer: {
    padding: "10px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: 5,
    overflowX: "auto",
    "& .ant-table-cell": {
      backgroundColor: "#FFFFFF",
    },
    "& .MuiChip-avatarSmall": {
      width: "12px",
      height: "12px",
    },
    "& .MuiChip-filled.MuiChip-sizeSmall": {
      height: "22px",
      fontSize: "0.7125rem",
    },
    "& .MuiAvatar-root": {
      height: "20px",
      width: "20px",
      fontSize: "12px",
      display: "inline-flex",
      marginLeft: "5px",
    },
  },
  TextCenter: {
    textAlign: "center !important",
  },
  dqmProgress: {
    "& .ant-progress-inner, .ant-progress-bg": {
      background: "#d32f2f",
    },
    "& .ant-progress-show-info .ant-progress-outer": {
      marginRight: 0,
      paddingRight: 0,
    },
    "& .ant-progress-text": {
      marginLeft: 0,
    },
    "& .ant-progress-success-bg": {
      backgroundColor: "#1B5E20",
    },
  },
  HealthPercentage: {
    cursor: "pointer",
    display: "flex",
    flexDirection: "column-reverse",
    alignItems: "center",
  },
  TextLeft: {
    textAlign: "left !important",
  },
  DragHandle: {
    cursor: "move !important",
    color: "#000000de",
    "&:active": {
      color: "#096dd9",
    },
    "&:hover": {
      color: "#40a9ff",
    },
    "&:active,:focus,:hover": {
      outline: 0,
      textDecoration: "none",
    },
  },
  IconHolder: {
    height: "25px",
    width: "25px",
    borderRadius: "50px",
    margin: "0",
    padding: "5px 5px",
    fontSize: "19px",
  },
  breakWord: {
    wordBreak: "break-all",
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  deleteReffConfirm: {
    "& span": {
      display: "block",
    },
  },
  InfoNoDV: {
    backgroundColor: "#ffe6e6",
    mt: 1,
    mb: 2,
    padding: "5px",
    paddingLeft: "10px",
  },
  Alert: {
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: 20,
  },
});

const AttributesTables = ({
  Workspace_id,
  accessRole,
  tableDataLoading,
  setDSInfoState,
  IsDataVaultEnabled,
  newSource,
  WorkspaceName,
  dataTableRef,
  tagsRef,
}) => {
  const { loading, fields, setFields, name: currentDatasource, datasetType } = useContext(DSContext);
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const entititData = useSelector((state) => state.entititData);
  const dataSourceData = useSelector((state) => state.dataSourceData);
  const { status: enStatus } = useMemo(() => entititData, [entititData]);
  const { status: dsStatus } = useMemo(() => dataSourceData, [dataSourceData]);

  const [currentField, setCurrentField] = useState("");
  const [currentMapping, setCurrentMapping] = useState({});
  const [selectedFields, setSelectedFields] = useState([]);
  const [openTab, setOpenTab] = useState("details");

  const [openAddMapping, setOpenAddMapping] = useState(false);
  const [openAddField, setOpenAddField] = useState(false);
  const [openTemplatePopup, setOpenTemplatePopup] = useState(false);
  const [openUploadPopup, setOpenUploadPopup] = useState(false);
  const [openErrorLogPopup, setOpenErrorLogPopup] = useState(false);

  const [templateName, setTemplateName] = useState("");
  const [errors, setErrors] = useState([]);

  const [loadFieldsOptions, setLoadFieldsOptions] = useState(null);
  const [notAllowedDialog, setNotAllowedDialog] = useState({
    openConfirm: false,
    confirmMessage: "",
    title: "",
  });
  const [deleteState, setDeleteState] = useState({
    openConfirm: false,
    confirmTitle: "",
    confirmMessage: "",
    handleClick: () => {},
    loadingForConfirm: false,
    confirmBtnName: "",
    confirmBtnColor: "",
  });

  const { $containsIsDeleted, $hasDVKey } = useMemo(() => {
    return {
      $containsIsDeleted: fields.some((field) => field?.isDeleted && field?.isDeleted?.length > 0),
      $hasDVKey: fields.some((field) => field.dataVaultKey),
    };
  }, [fields]);

  const templateDataRef = useRef([]);
  const currentDatasourceRef = useRef("");
  const fieldsRef = useRef([]);

  const open = Boolean(loadFieldsOptions);

  const handleClick = useCallback((event) => {
    setLoadFieldsOptions(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setLoadFieldsOptions(null);
  }, []);

  useEffect(() => {
    currentDatasourceRef.current = currentDatasource;
  }, [currentDatasource]);

  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  // Close Delete Selected Fields
  const deleteFields = () => {
    dataTableRef.current.deleteRow(selectedFields);
    dispatch(
      showToast({
        message: "Selected field(s) deleted successfully",
        type: "success",
      })
    );

    setPrevState(setDeleteState, { openConfirm: false, confirmBtnName: "", confirmBtnColor: "" });
  };

  const onChangeEvent = useCallback(
    (data) => {
      let { action, dataSource } = data;
      if (action === "add") {
        dataSource = Array.isArray(dataSource) ? dataSource : [dataSource];
        setFields((prev) => [
          ...prev,
          ...dataSource.map((_field) => (_field.id ? _field : { id: getUUID(), ..._field })),
        ]);
      } else if (action === "update") {
        const { oldName } = data;

        setFields((prev) => prev.map((field) => (field.name === oldName ? dataSource : field)));
        setSelectedFields((prev) => prev.map((field) => (field.name === oldName ? dataSource : field)));
      } else if (action === "delete") {
        //When there was reference but it got removed
        dataSource.forEach((_field) => {
          if (
            _field.referenceFrom &&
            Object.keys(_field.referenceFrom).length > 0 &&
            _field.referenceFrom.data_source === currentDatasourceRef.current
          ) {
            const refFromField = _field.referenceFrom.field.name;

            const tegToField = fieldsRef.current.find((field) => field.name === refFromField);

            tegToField.referenceTo = tegToField.referenceTo.filter(
              ({ data_source, field }) => data_source === currentDatasourceRef.current && field === currentField
            );
            dataTableRef.current.updateRow(refFromField, {
              ...tegToField,
            });
          }
        });

        setFields((prev) =>
          prev
            .filter((field) => !dataSource.some((source) => source.name === field.name))
            .map((field, index) => ({ ...field, key: index + 1 }))
        );
        setSelectedFields((prev) => prev.filter((field) => !dataSource.some((source) => source.name === field.name)));
      } else if (action === "drag") {
        setFields(dataSource.map(({ key, ...other }) => other));
        setSelectedFields([]);
      } else if (action === "select") {
        const { selectedRow } = data;
        setSelectedFields(selectedRow.map(({ key, ...other }) => other));
      }

      return [];
    },
    [currentField, setFields]
  );

  //Handle Adding Field
  const addField = (value) => {
    const tempFields = JSON.parse(JSON.stringify(fields));

    //Checking if Updating or adding new
    if (currentField) {
      const tField = fields.find((field) => field.name === currentField);

      //When there was reference but it got removed
      if (
        tField.referenceFrom &&
        Object.keys(tField.referenceFrom).length > 0 &&
        tField.referenceFrom.data_source === currentDatasource &&
        (tField.referenceFrom.data_source !== value.referenceFrom?.data_source ||
          tField.referenceFrom.field.name !== value.referenceFrom?.field.name)
      ) {
        const refFromField = tField.referenceFrom.field.name;
        const tegToField = tempFields.find((field) => field.name === refFromField);

        tegToField.referenceTo = tegToField.referenceTo.filter(
          ({ data_source, field }) => data_source === currentDatasource && field === currentField
        );
        dataTableRef.current.updateRow(refFromField, {
          ...tegToField,
        });
      }

      const isRenamed = tField.name !== value.name;

      //When there is reference but it get renamed
      if (
        isRenamed &&
        tField.referenceFrom &&
        Object.keys(tField.referenceFrom).length > 0 &&
        tField.referenceFrom.data_source === currentDatasource
      ) {
        const _field = tempFields.find(({ name }) => name === tField.referenceFrom.field.name);

        _field.referenceTo = _field.referenceTo.map(({ data_source, field }) =>
          data_source === currentDatasource && field.name === tField.name
            ? { data_source, field: { ...field, name: value.name } }
            : { data_source, field }
        );

        dataTableRef.current.updateRow(_field.name, {
          ..._field,
        });
      }

      //Where there is reference to and it get renamed
      if (isRenamed && tField.referenceTo?.length) {
        const fieldsToUpdate = [];

        tField.referenceTo.forEach(({ data_source, field }) => {
          if (data_source === currentDatasource) fieldsToUpdate.push(field.name);
        });

        for (const field of fieldsToUpdate) {
          const _field = tempFields.find(({ name }) => name === field);
          _field.referenceFrom = {
            data_source: _field.referenceFrom.data_source,
            field: {
              ..._field.referenceFrom.field,
              name: value.name,
            },
          };

          dataTableRef.current.updateRow(_field.name, {
            ..._field,
          });
        }
      }

      dataTableRef.current.updateRow(currentField, {
        ...tField,
        ...value,
        ...(newSource ? {} : { modified: true }),
      });
      setCurrentField(value.name);
    } else {
      dataTableRef.current.addRow({
        ...value,
        entity_mapping: {},
        dqm: { required: false },
        required: true,
      });
    }

    if (
      value.referenceFrom &&
      Object.keys(value.referenceFrom).length > 0 &&
      value.referenceFrom.data_source === currentDatasource
    ) {
      const refFromField = value.referenceFrom.field.name;
      const tField = tempFields.find((field) => field.name === refFromField);

      if (!tField.referenceTo) tField.referenceTo = [];

      //Adding only if its not exists
      if (!tField.referenceTo.some(({ field }) => field.name === value.name)) {
        tField.referenceTo.push({
          data_source: currentDatasource,
          field: {
            name: value.name,
            type: value.referenceFrom.field.type,
            length: value.referenceFrom.field.length,
            format: value.referenceFrom.field.format || "",
            customFormat: value.referenceFrom.field.customFormat || "",
          },
        });

        dataTableRef.current.updateRow(refFromField, {
          ...tField,
        });
      }
    }
  };

  //Handle Adding of Mapping
  const addMapping = async (newValue) => {
    setOpenAddMapping(false);

    setCurrentMapping((prev) => ({
      ...newValue,
      fieldName: prev.fieldName,
    }));

    const newField = fields.find((field) => field.name === currentMapping.fieldName);
    newField.entity_mapping = newValue;

    // Remove designate field, referenceTo object if user removes mapping
    if (!Object.keys(newValue).length) {
      if (newField.primary_attribute) newField.primary_attribute = false;
      if (newField.secondary_attribute) newField.secondary_attribute = false;
    }

    dataTableRef.current.updateRow(newField.name, newField);
  };

  const fetchENTRequiredInfo = useCallback(async () => {
    if (enStatus === "notFetched") {
      dispatch(setEntityFetching());
      setPrevState(setDSInfoState, { loading: true });

      const { data, success } = await new DataSourceClass().getObjectRequiredInfo(Workspace_id, OBJECT_TYPE.Entity);

      if (success) {
        const entities = JSON.parse(data) ?? [];
        const sorted = entities.sort((a, b) => a.name.localeCompare(b.name));
        dispatch(addEntities(sorted));
      }
      setPrevState(setDSInfoState, { loading: false });
    }
  }, [Workspace_id, dispatch, enStatus, setDSInfoState]);

  const fetchDSRequiredInfo = useCallback(async () => {
    if (dsStatus === "notFetched") {
      dispatch(setDsFetching());
      setPrevState(setDSInfoState, { loading: true });

      const { data, success } = await new DataSourceClass().getObjectRequiredInfo(Workspace_id, OBJECT_TYPE.DataSource);

      if (success) {
        const datasources = JSON.parse(data) ?? [];
        const sorted = datasources.sort((a, b) => a.name.localeCompare(b.name));
        dispatch(addDataSources(sorted));
      }

      setPrevState(setDSInfoState, { loading: false });
    }
  }, [Workspace_id, dispatch, dsStatus, setDSInfoState]);

  // Close Delete Confirm Popup
  const handleCancel = useCallback(() => {
    setPrevState(setDeleteState, { openConfirm: false, confirmBtnName: "", confirmBtnColor: "" });
  }, []);

  //Generating columns
  const $columns = useMemo(() => {
    return IsDataVaultEnabled
      ? [
          {
            dataIndex: "drag",
            title: "",
            render: (text, record, index) => (
              <span className={classes.DragHandle}>
                <MenuOutlined />
              </span>
            ),
            className: classes.TextCenter,
            width: 46,
          },
          {
            dataIndex: "key",
            title: "Column",
            className: [classes.TextCenter],
            width: 120,
            render: (text, record) => {
              return (
                <>
                  <Tooltip placement="top" title={record.description}>
                    <IconButton
                      size="small"
                      disabled={record.description ? false : true}
                      color="success"
                      sx={{ mr: 1 }}
                    >
                      <InfoCircleFilled />
                    </IconButton>
                  </Tooltip>
                  {text}
                </>
              );
            },
          },
          {
            dataIndex: "name",
            title: "Attribute",
            className: classes.TextLeft + " " + classes.breakWord,
            width: 300,
            // searchColumn: true,
            render: (text, record) => {
              return (
                <>
                  {text}
                  {!!record.dataVaultKey ? (
                    <Tooltip placement="top" title="Datavault Key">
                      <Avatar
                        className={classes.IconHolder}
                        sx={{
                          backgroundColor: "#2A6CA0",
                        }}
                      >
                        K
                      </Avatar>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                  {record?.isDerived ? (
                    <Tooltip placement="top" title="Derived Value">
                      <Avatar
                        className={classes.IconHolder}
                        sx={{
                          backgroundColor: "#00BDBD",
                        }}
                      >
                        V
                      </Avatar>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                  {record?.isDeleted && record?.isDeleted?.length > 0 ? (
                    <Tooltip placement="top" title="Deletion Flag">
                      <Avatar
                        className={classes.IconHolder}
                        sx={{
                          backgroundColor: "#929292",
                        }}
                      >
                        D
                      </Avatar>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                </>
              );
            },
          },
          {
            dataIndex: "type",
            title: "Type",
            className: classes.TextLeft,
            width: 100,
          },
          {
            dataIndex: "length",
            title: "Length/Format",
            className: classes.TextCenter,
            width: 130,
            render: (text, field) => {
              if (field && Object.keys(field).length) {
                if (field.type !== "Date" && field.type !== "DateTime" && field.type !== "Time") return field.length;
                else if (field.format !== undefined)
                  return field.format !== "Custom" ? field.format : field.customFormat;
              }
            },
          },
          {
            dataIndex: "reference",
            title: "Reference",
            className: classes.TextCenter,
            width: 230,
            render: (text, field) => {
              const refArr = [];
              //Logic to Fetch Reference
              if (field && Object.keys(field).length) {
                //Logic to Fetch Reference From(As Child) Data
                if (field.referenceFrom?.field && Object.keys(field.referenceFrom?.field).length)
                  refArr.push({ ...field.referenceFrom, dir: "Up" });
                //Logic to Fetch Reference To(As Parent) Data
                if (field.referenceTo && field.referenceTo.length) {
                  field.referenceTo.forEach((obj) => {
                    refArr.push({ data_source: obj.data_source, field: obj.field, dir: "Down" });
                  });
                }
              }
              return (
                <div style={{ textAlign: "left", maxWidth: "250px" }}>
                  {refArr.length
                    ? refArr.map((refObj, index) => (
                        <span
                          key={index}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100px",
                          }}
                        >
                          <Grid container>
                            <Grid size={7}>
                              <span
                                style={{ whiteSpace: "pre-line" }}
                              >{`${refObj.data_source} | ${refObj.field.name}`}</span>
                            </Grid>
                            <Grid
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                paddingLeft: "5px",
                              }}
                              size={5}
                            >
                              <Chip
                                label={refObj.dir === "Up" ? "RF" : "RT"}
                                size="small"
                                variant="filled"
                                color="success"
                                icon={refObj.dir === "Up" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                              />
                            </Grid>
                          </Grid>
                        </span>
                      ))
                    : null}
                </div>
              );
            },
          },
          {
            dataIndex: "entity_mapping",
            title: "Entity",
            className: classes.TextCenter + " " + classes.breakWord,
            width: 215,
            render: (mapping, field) => {
              if (field?.isDeleted && field?.isDeleted?.length > 0) {
                return <></>;
              } else if (
                mapping &&
                Object.keys(mapping).length &&
                mapping.hasOwnProperty("entity") &&
                mapping.hasOwnProperty("field")
              ) {
                return (
                  <div style={{ textAlign: "left" }}>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100px",
                      }}
                    >
                      <Grid container>
                        <Grid size={9}>
                          <span style={{ whiteSpace: "pre-line" }}>
                            {mapping.entity} | {mapping.field}
                          </span>
                        </Grid>
                        <Grid
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            paddingLeft: "8px",
                          }}
                          size={3}
                        >
                          <IconButton
                            className={classes.IconHolder}
                            onClick={() => {
                              setCurrentMapping({
                                fieldName: field.name,
                                fieldType: field.type === "Reference" ? field.referenceFrom.field.type : field.type,
                                fieldLength:
                                  field.length === "Reference" ? field.referenceFrom.field.length : field.length,
                                entity: mapping.entity,
                                field: mapping.field,
                              });
                              setOpenAddMapping(true);
                            }}
                            disabled={datasetType === "readonly"}
                          >
                            <EditOutlinedIcon
                              color="primary"
                              fontSize="inherit"
                              disabled={datasetType === "readonly"}
                            />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </span>
                  </div>
                );
              } else {
                return (
                  <div>
                    <Grid container>
                      <Grid size={9}></Grid>
                      <Grid
                        sx={{ display: "flex", alignItems: "center", justifyContent: "center", paddingLeft: "8px" }}
                        size={3}
                      >
                        <IconButton
                          className={classes.IconHolder}
                          style={{
                            ...(datasetType === "readonly" ? {} : { backgroundColor: "#C7DCFF", color: "#1A57BD" }),
                          }}
                          onClick={() => {
                            setCurrentMapping({
                              fieldName: field.name,
                              fieldType: field.type === "Reference" ? field.referenceFrom.field.type : field.type,
                              fieldLength:
                                field.length === "Reference" ? field.referenceFrom.field.length : field.length,
                            });
                            setOpenAddMapping(true);
                          }}
                          disabled={datasetType === "readonly"}
                        >
                          <NodeIndexOutlined fontSize="inherit" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                );
              }
            },
          },
          {
            dataIndex: "lov",
            title: "LoV",
            className: [classes.TextCenter],
            width: 45,
            render: (text, record) => {
              return record.lov?.enabled ? (
                <Chip
                  label="LoV"
                  size="small"
                  variant="filled"
                  color="success"
                  avatar={<Avatar sx={{ bgcolor: "#529B28" }} src="/images/list_chip.png" />}
                  onClick={() => {
                    setOpenTab("LoV");
                    setCurrentField(record.name);
                    setOpenAddField(true);
                  }}
                />
              ) : (
                ""
              );
            },
          },
          {
            dataIndex: "dqm",
            title: "DQM",
            className: classes.TextCenter + " " + classes.dqmProgress,
            width: 100,
            render: (value, record) => {
              return value && value.required ? (
                value.HealthPercentage !== undefined ? (
                  <Progress
                    percent={value.HealthPercentage}
                    success={{
                      percent: value.HealthPercentage,
                    }}
                    className={classes.HealthPercentage}
                    size="small"
                    status="active"
                    onClick={() => {
                      setOpenTab("DQM");
                      setCurrentField(record.name);
                      setOpenAddField(true);
                    }}
                  />
                ) : (
                  <Chip
                    color="info"
                    size="small"
                    style={{ cursor: "pointer", backgroundColor: "#EEB680" }}
                    icon={<SportsScoreIcon />}
                    label="DQM"
                    onClick={() => {
                      setOpenTab("DQM");
                      setCurrentField(record.name);
                      setOpenAddField(true);
                    }}
                  />
                )
              ) : (
                ""
              );
            },
          },
          {
            dataIndex: "Action",
            title: "",
            className: classes.TextLeft,
            width: 90,
            render: (value, record, index) => {
              return (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton
                    className={classes.IconHolder}
                    onClick={() => {
                      setOpenTab("details");
                      setCurrentField(record.name);
                      setOpenAddField(true);
                    }}
                  >
                    <EditOutlinedIcon color="primary" fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    className={classes.IconHolder}
                    onClick={(e) => {
                      if (record.referenceTo && record.referenceTo.length) {
                        const refrDatasources = new Set();
                        record.referenceTo.forEach(({ data_source }) => refrDatasources.add(data_source));
                        setPrevState(setNotAllowedDialog, {
                          openConfirm: true,
                          title: "Action not allowed",
                          confirmMessage: (
                            <span className={classes.deleteReffConfirm}>
                              <span>
                                You are trying to delete a parent attribute which is referencing to following{" "}
                                {DATA_SOURCE.plural.toLowerCase()}:
                              </span>
                              <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                                {[...refrDatasources].map((data_source) => (
                                  <ListItem sx={{ display: "list-item", p: 0 }} key={data_source}>
                                    <ListItemText primary={data_source} />
                                  </ListItem>
                                ))}
                              </List>
                              <span>You need to remove those references to perform this action.</span>
                            </span>
                          ),
                        });
                        return;
                      }
                      dataTableRef.current.deleteRow([record]);
                    }}
                    style={{ marginLeft: "5px", ...(datasetType === "readonly" ? {} : { color: "#C72C2C" }) }}
                    disabled={datasetType === "readonly"}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </div>
              );
            },
          },
        ]
      : [
          {
            dataIndex: "drag",
            title: "",
            render: (text, record, index) => (
              <span className={classes.DragHandle}>
                <MenuOutlined />
              </span>
            ),
            className: classes.TextCenter,
            width: 46,
          },
          {
            dataIndex: "key",
            title: "Column",
            className: [classes.TextCenter],
            width: 120,
            render: (text, record) => {
              return (
                <>
                  <Tooltip placement="top" title={record.description}>
                    <IconButton size="small" disabled={!record.description} color="success" sx={{ mr: 1 }}>
                      <InfoCircleFilled />
                    </IconButton>
                  </Tooltip>
                  {text}
                </>
              );
            },
          },
          {
            dataIndex: "name",
            title: "Attribute",
            className: classes.TextLeft + " " + classes.breakWord,
            width: 300,
            // searchColumn: true,
            render: (text, record) => {
              return (
                <>
                  {text}
                  {record.primary_attribute || record.secondary_attribute ? (
                    <Tooltip placement="top" title={record.primary_attribute ? "Primary" : "Secondary"}>
                      <Avatar
                        className={classes.IconHolder}
                        sx={{
                          backgroundColor: (theme) =>
                            record.primary_attribute ? theme.palette.success.main : theme.palette.secondary.main,
                        }}
                      >
                        {record.primary_attribute ? "P" : "S"}
                      </Avatar>
                    </Tooltip>
                  ) : (
                    ""
                  )}
                </>
              );
            },
          },
          {
            dataIndex: "type",
            title: "Type",
            className: classes.TextLeft,
            width: 100,
          },
          {
            dataIndex: "length",
            title: "Length/Format",
            className: classes.TextCenter,
            width: 130,
            render: (text, field) => {
              if (field && Object.keys(field).length) {
                if (field.type !== "Date" && field.type !== "DateTime" && field.type !== "Time") return field.length;
                else if (field.format !== undefined)
                  return field.format !== "Custom" ? field.format : field.customFormat;
              }
            },
          },
          {
            dataIndex: "entity_mapping",
            title: "Entity",
            className: classes.TextCenter + " " + classes.breakWord,
            width: 215,
            render: (mapping, field) => {
              if (mapping && Object.keys(mapping).length) {
                return (
                  <div style={{ textAlign: "left" }}>
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100px",
                      }}
                    >
                      <Grid container>
                        <Grid size={9}>
                          <span style={{ whiteSpace: "pre-line" }}>
                            {mapping.entity} | {mapping.field}
                          </span>
                        </Grid>
                        <Grid
                          sx={{ display: "flex", alignItems: "center", justifyContent: "center", paddingLeft: "8px" }}
                          size={3}
                        >
                          <IconButton
                            className={classes.IconHolder}
                            onClick={() => {
                              setCurrentMapping({
                                fieldName: field.name,
                                fieldType: field.type === "Reference" ? field.referenceFrom.field.type : field.type,
                                fieldLength:
                                  field.length === "Reference" ? field.referenceFrom.field.length : field.length,
                                entity: mapping.entity,
                                field: mapping.field,
                              });
                              setOpenAddMapping(true);
                            }}
                          >
                            <EditOutlinedIcon color="primary" fontSize="inherit" />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </span>
                  </div>
                );
              } else {
                return (
                  <div>
                    <Grid container>
                      <Grid size={9}></Grid>
                      <Grid
                        sx={{ display: "flex", alignItems: "center", justifyContent: "center", paddingLeft: "8px" }}
                        size={3}
                      >
                        <IconButton
                          className={classes.IconHolder}
                          style={{ backgroundColor: "#C7DCFF", color: "#1A57BD" }}
                          onClick={() => {
                            setCurrentMapping({
                              fieldName: field.name,
                              fieldType: field.type === "Reference" ? field.referenceFrom.field.type : field.type,
                              fieldLength:
                                field.length === "Reference" ? field.referenceFrom.field.length : field.length,
                            });
                            setOpenAddMapping(true);
                          }}
                        >
                          <NodeIndexOutlined fontSize="inherit" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </div>
                );
              }
            },
          },
          {
            dataIndex: "lov",
            title: "LoV",
            className: [classes.TextCenter],
            width: 45,
            render: (text, record) => {
              return record.lov?.enabled ? (
                <Chip
                  label="LoV"
                  size="small"
                  variant="filled"
                  color="success"
                  avatar={<Avatar sx={{ bgcolor: "#529B28" }} src="/images/list_chip.png" />}
                  onClick={() => {
                    setOpenTab("LoV");
                    setCurrentField(record.name);
                    setOpenAddField(true);
                  }}
                />
              ) : (
                ""
              );
            },
          },
          {
            dataIndex: "dqm",
            title: "DQM",
            className: classes.TextCenter,
            width: 100,
            render: (value, record) => {
              return value && value.required ? (
                <Chip
                  color="info"
                  size="small"
                  style={{ cursor: "pointer", backgroundColor: "#EEB680" }}
                  icon={<SportsScoreIcon />}
                  label="DQM"
                  onClick={() => {
                    setOpenTab("DQM");
                    setCurrentField(record.name);
                    setOpenAddField(true);
                  }}
                />
              ) : (
                ""
              );
            },
          },
          {
            dataIndex: "Action",
            title: "",
            className: classes.TextLeft,
            width: 125,
            render: (value, record, index) => {
              return (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton
                    className={classes.IconHolder}
                    onClick={() => {
                      setOpenTab("details");
                      setCurrentField(record.name);
                      setOpenAddField(true);
                    }}
                  >
                    <EditOutlinedIcon color="primary" fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    className={classes.IconHolder}
                    onClick={(e) => {
                      if (record.referenceTo && record.referenceTo.length) {
                        const refrDatasources = new Set();
                        record.referenceTo.forEach(({ data_source }) => refrDatasources.add(data_source));
                        setPrevState(setNotAllowedDialog, {
                          openConfirm: true,
                          title: "Action not allowed",
                          confirmMessage: (
                            <span className={classes.deleteReffConfirm}>
                              <span>
                                You are trying to delete a parent attribute which is referencing to following{" "}
                                {DATA_SOURCE.plural.toLowerCase()}:
                              </span>
                              <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                                {[...refrDatasources].map((data_source) => (
                                  <ListItem sx={{ display: "list-item", p: 0 }} key={data_source}>
                                    <ListItemText primary={data_source} />
                                  </ListItem>
                                ))}
                              </List>
                              <span>You need to remove those references to perform this action.</span>
                            </span>
                          ),
                        });
                        return;
                      }
                      dataTableRef.current.deleteRow([record]);
                    }}
                    style={{
                      marginLeft: "5px",
                      color: "#C72C2C",
                    }}
                  >
                    <DeleteOutlineOutlinedIcon fontSize="inherit" />
                  </IconButton>
                </div>
              );
            },
          },
        ];
  }, [
    IsDataVaultEnabled,
    classes.DragHandle,
    classes.HealthPercentage,
    classes.IconHolder,
    classes.TextCenter,
    classes.TextLeft,
    classes.breakWord,
    classes.deleteReffConfirm,
    classes.dqmProgress,
    dataTableRef,
    datasetType,
  ]);

  return (
    <>
      <Grid container direction="column" justifyContent="flex-end" alignItems="left">
        <Grid className={classes.PanelContainer}>
          <SmallToolbar>Attributes</SmallToolbar>

          <Grid container direction="row" justifyContent="flex-start" spacing={3}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Stack spacing={2} direction="row" sx={{ mb: "10px" }}>
                <ButtonUI
                  endIcon={<AddIcon />}
                  variant="contained"
                  color="info"
                  style={{ width: "150px" }}
                  onClick={() => {
                    setOpenTab("details");
                    setCurrentField("");
                    setOpenAddField(true);
                  }}
                  disabled={datasetType === "readonly" || tableDataLoading}
                >
                  <span style={{ marginTop: "2px" }}>Add Field</span>
                </ButtonUI>
                <ButtonUI
                  variant="contained"
                  color="error"
                  style={{ marginLeft: 15 }}
                  disabled={!selectedFields.length || datasetType === "readonly"}
                  onClick={(e) => {
                    const referenceTos = selectedFields.filter((field) => field.referenceTo?.length);

                    if (referenceTos.length) {
                      const refrDatasources = new Set();
                      referenceTos.forEach((refr) => {
                        refr.referenceTo.forEach(({ data_source }) => refrDatasources.add(data_source));
                      });

                      setPrevState(setNotAllowedDialog, {
                        openConfirm: true,
                        title: "Action not allowed",
                        confirmMessage: (
                          <span className={classes.deleteReffConfirm}>
                            <span>
                              You are trying to delete a parent attribute which is referencing to following{" "}
                              {DATA_SOURCE.plural.toLowerCase()}:
                            </span>
                            <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                              {[...refrDatasources].map((data_source) => (
                                <ListItem sx={{ display: "list-item", p: 0 }} key={data_source}>
                                  <ListItemText primary={data_source} />
                                </ListItem>
                              ))}
                            </List>
                            <span>You need to remove those references to perform this action.</span>
                          </span>
                        ),
                      });
                      return;
                    }

                    setPrevState(setDeleteState, {
                      openConfirm: true,
                      confirmTitle: "Delete Attribute",
                      confirmMessage: `Are you sure you want to delete selected field(s)?`,
                      handleClick: deleteFields,
                    });
                  }}
                >
                  <span style={{ marginTop: "2px" }}>Delete</span>
                </ButtonUI>
              </Stack>
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <Stack spacing={2} direction="row" sx={{ mb: "10px" }} justifyContent="flex-end">
                {[1, 2].includes(accessRole) ? (
                  <>
                    <IconButton onClick={handleClick}>
                      <MoreHorizIcon fontSize="inherit" />
                    </IconButton>
                    <Menu
                      id="basic-menu"
                      anchorEl={loadFieldsOptions}
                      open={open}
                      onClose={handleMenuClose}
                      MenuListProps={{
                        "aria-labelledby": "basic-button",
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          const referenceTos = fields.filter((field) => field.referenceTo?.length);
                          if (referenceTos.length > 0) {
                            const refrDatasources = new Set();
                            referenceTos.forEach((refr) => {
                              refr.referenceTo.forEach(({ data_source }) => refrDatasources.add(data_source));
                            });

                            setPrevState(setNotAllowedDialog, {
                              openConfirm: true,
                              title: "Warning",
                              confirmMessage: (
                                <span className={classes.deleteReffConfirm}>
                                  <span>
                                    You are trying to overwrite the {DATA_SOURCE.singular.toLowerCase()} containing
                                    parent attribute(s) which is referencing to following{" "}
                                    {DATA_SOURCE.singular.toLowerCase()}
                                    (s):
                                  </span>
                                  <List sx={{ listStyleType: "disc", pl: 4, background: "#f0f0f0" }} dense={true}>
                                    {[...refrDatasources].map((data_source) => (
                                      <ListItem sx={{ display: "list-item", p: 0 }} key={data_source}>
                                        <ListItemText primary={data_source} />
                                      </ListItem>
                                    ))}
                                  </List>
                                  <span>
                                    Please remove those relationships to overwrite this{" "}
                                    {DATA_SOURCE.singular.toLowerCase()}.
                                  </span>
                                </span>
                              ),
                            });
                          } else {
                            setOpenUploadPopup(true);
                          }
                          handleMenuClose();
                        }}
                        disabled={datasetType === "readonly" || tableDataLoading}
                      >
                        <ListItemIcon>
                          <FileDownloadIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Load From Template</ListItemText>
                      </MenuItem>
                      <MenuItem
                        onClick={() => setOpenTemplatePopup(true)}
                        disabled={!(fields.length > 0) || tableDataLoading}
                      >
                        <ListItemIcon>
                          <SaveIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Save Template</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                ) : null}
              </Stack>
            </Grid>
          </Grid>

          {!tableDataLoading && ["readonly", "untracked"].includes(datasetType) && (
            <Stack className="switch" sx={{ width: "100%", marginBottom: "10px" }} spacing={2}>
              <Alert
                icon={<InfoOutlinedIcon color="error" fontSize="inherit" />}
                className={classes.InfoNoDV}
                variant="outlined"
                severity="error"
              >
                {datasetType === "readonly" ? "'Read-Only Data View' " : "'Untracked' "}
                is selected. History for the dataset will not be tracked. It cannot be used in Entity Mapping, and any
                previously configured Entity Mappings will be cleared. DQM will be supported only when a Data Vault Key
                is defined.
              </Alert>
            </Stack>
          )}

          <Box className={classes.DataTableContainer}>
            <DraggableTable
              data={fields}
              column={$columns}
              rowSelection={true}
              onChange={onChangeEvent}
              ref={dataTableRef}
              key={IsDataVaultEnabled}
              loading={tableDataLoading}
            />
          </Box>
        </Grid>
      </Grid>
      {notAllowedDialog.openConfirm && (
        <NotAllowedDialog
          notAllowedDialog={notAllowedDialog}
          onClose={() => {
            setNotAllowedDialog({
              openConfirm: false,
              confirmMessage: "",
              title: "",
            });
          }}
        />
      )}
      {deleteState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: deleteState.confirmTitle,
            popupMessage: deleteState.confirmMessage,
            handleCancel: deleteState.handleCancel || handleCancel,
            handleClick: deleteState.handleClick,
            loadingForConfirm: deleteState.loadingForConfirm,
            confirmBtnName: deleteState.confirmBtnName,
            confirmBtnColor: deleteState.confirmBtnColor,
          }}
        />
      )}
      {openUploadPopup && (
        <UploadTemplate
          setOpen={setOpenUploadPopup}
          setFields={setFields}
          dataTableRef={dataTableRef}
          tagsRef={tagsRef}
          note="The current tags will be overridden."
          type={1}
          setErrors={setErrors}
          setOpenErrorLogPopup={setOpenErrorLogPopup}
          setTemplateName={setTemplateName}
          templateDataRef={templateDataRef}
          workspaceName={WorkspaceName}
          fetchENTRequiredInfo={fetchENTRequiredInfo}
          fetchDSRequiredInfo={fetchDSRequiredInfo}
          IsDataVaultEnabled={IsDataVaultEnabled}
        />
      )}
      {openErrorLogPopup && (
        <ErrorLog
          setOpenErrorLogPopup={setOpenErrorLogPopup}
          popupTitle="Template Error Log"
          errorList={errors}
          dataTableRef={dataTableRef}
          templateDataRef={templateDataRef}
          templateName={templateName}
          currentFields={fields}
          tagsRef={tagsRef}
        />
      )}
      {!loading && openAddField && (
        <AddField
          setOpenAddField={setOpenAddField}
          addField={addField}
          currentField={currentField}
          dataTableRef={dataTableRef}
          openTab={openTab}
          IsDataVaultEnabled={IsDataVaultEnabled}
          containsIsDeleted={$containsIsDeleted}
          setCurrentField={setCurrentField}
          newDatasource={newSource}
          fetchDSRequiredInfo={fetchDSRequiredInfo}
        />
      )}
      {!loading && openAddMapping && (
        <AddMapping
          currentMapping={currentMapping}
          addMapping={addMapping}
          fetchENTRequiredInfo={fetchENTRequiredInfo}
        />
      )}
      {openTemplatePopup && (
        <Template
          Settings={{
            open: openTemplatePopup,
            setOpen: setOpenTemplatePopup,
            dataTableRef,
            tagsRef,
            type: 1,
          }}
        />
      )}
    </>
  );
};

export default AttributesTables;
